import React, { Component } from 'react'
import { Button, Descriptions, Form, FormInstance, Select, Tag } from 'antd'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'
import MappingStepper, { Step } from '../MappingStepper'
import { SpecieConsumer, SpecieContextData } from '../../../../context/SpecieContext'
import { ShelterConsumer, ShelterContextData } from 'context/ShelterContext'
import { sbListAnimalClass, sbListAnimalType } from 'services/integration'
import ShelterBuddyExternalField, { ExternalOption } from '../ShelterBuddyExternalField'
import { DIRECTION_LABEL, Flow, Partner } from '../shared/types'
import { resolveFlow } from '../shared/resolvers'
import { makeSbLoader, mapSbOptions, ensureSbOk } from '../shared/sbOptions'
import { ActiveField, IncomingStep, IntegrationField, ManualExternalInputs, PartnerField, ScopeField } from '../shared/fields'

/** Loads the Shelter Buddy animal class list for an integration. */
const loadAnimalClasses = makeSbLoader(sbListAnimalClass, 'animal classes')

type AnimalTypeFieldProps = {
  form: FormInstance
  integrationUuid: string
}

type AnimalTypeFieldState = {
  fetchingClasses: boolean
  classList: ExternalOption[] | undefined
  classError: string | undefined
  /** Classes currently picked in the multi-select (not yet committed). */
  selectedClassIds: string[]
  /** Classes the type list is actually loaded for — set on blur/button. */
  committedClassIds: string[] | undefined
}

/**
 * Shelter Buddy animal type picker. Animal types are scoped by one or more animal
 * classes, so the user first picks the classes (multi-select loaded via
 * `sbListAnimalClass`); only after committing that selection (on blur or via the
 * "Load types" button) is the type list loaded via `sbListAnimalType`. The chosen
 * type is stored in `external_id`/`name` by the shared `ShelterBuddyExternalField`.
 */
class ShelterBuddyAnimalTypeField extends Component<AnimalTypeFieldProps, AnimalTypeFieldState> {
  state: AnimalTypeFieldState = {
    fetchingClasses: false,
    classList: undefined,
    classError: undefined,
    selectedClassIds: [],
    committedClassIds: undefined,
  }

  componentDidMount() {
    this.loadClasses()
  }

  componentDidUpdate(prevProps: AnimalTypeFieldProps) {
    // A different integration means a different class/type universe — reset.
    if (prevProps.integrationUuid !== this.props.integrationUuid) {
      this.setState({ selectedClassIds: [], committedClassIds: undefined }, this.loadClasses)
    }
  }

  loadClasses = () => {
    const { integrationUuid } = this.props
    if (!integrationUuid) return

    this.setState({ fetchingClasses: true, classError: undefined }, async () => {
      try {
        const classList = await loadAnimalClasses(integrationUuid)
        this.setState({ fetchingClasses: false, classList, classError: undefined })
      } catch (e) {
        console.error(e)
        const message = (e as Error)?.message || 'Failed to load animal classes'
        this.setState({ fetchingClasses: false, classList: undefined, classError: message })
      }
    })
  }

  /** Loads the type list for the committed classes. Passed to ShelterBuddyExternalField. */
  loadTypes = async (integrationUuid: string): Promise<ExternalOption[]> => {
    const classIdList = (this.state.committedClassIds ?? []).map(Number)
    const response = await sbListAnimalType({
      integration_uuid: integrationUuid,
      filters: { class_id_list: classIdList },
    })

    ensureSbOk(response, 'animal types')

    return mapSbOptions(response.data.data)
  }

  /** Commit the selected classes so the type list can load. */
  commitClasses = () => {
    const { selectedClassIds, committedClassIds } = this.state
    if (!selectedClassIds.length) return

    // No change — nothing to reload.
    const sameSelection =
      committedClassIds?.length === selectedClassIds.length &&
      committedClassIds.every(id => selectedClassIds.includes(id))
    if (sameSelection) return

    // Keep the current type value — it stays selected until the user picks
    // another from the reloaded list.
    this.setState({ committedClassIds: [...selectedClassIds] })
  }

  handleRetryClasses = () => this.loadClasses()

  render() {
    const { form, integrationUuid } = this.props
    const { fetchingClasses, classList, classError, selectedClassIds, committedClassIds } = this.state

    const classHelp = classError ? (
      <span>
        {classError}{' '}
        <Button size="small" type="link" loading={fetchingClasses} onClick={this.handleRetryClasses}>
          Retry
        </Button>
      </span>
    ) : (
      'Pick the animal classes, then load the types.'
    )

    return (
      <>
        <Form.Item
          label="Animal Class"
          validateStatus={classError ? 'error' : undefined}
          help={classHelp}
        >
          <Select
            mode="multiple"
            loading={fetchingClasses}
            placeholder="Select one or more classes"
            options={classList}
            value={selectedClassIds}
            showSearch
            filterOption={DEFAULT_SELECT_FILTER_OPTION}
            onChange={(value: string[]) => this.setState({ selectedClassIds: value })}
            onBlur={this.commitClasses}
          />
          {/* <Button
            style={{ marginTop: 8 }}
            onClick={this.commitClasses}
            disabled={!selectedClassIds.length}
          >
            Load types
          </Button> */}
        </Form.Item>

        <ShelterBuddyExternalField
          form={form}
          integrationUuid={integrationUuid}
          loadExternalList={this.loadTypes}
          reloadKey={committedClassIds?.join(',') ?? ''}
          disabled={!committedClassIds?.length}
          label="Animal Type"
          placeholder="Select a type"
        />
      </>
    )
  }
}

type MappingSpecieFormProps = {
  form: FormInstance
  partnerList: Partner[] | null
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingSpecieFormInnerProps = MappingSpecieFormProps & {
  specieContext: SpecieContextData
  shelterContext: ShelterContextData
}

type MappingSpecieFormInnerState = {
  direction: Flow
}

class MappingSpecieFormInner extends Component<MappingSpecieFormInnerProps, MappingSpecieFormInnerState> {
  constructor(props: MappingSpecieFormInnerProps) {
    super(props)
    this.state = {
      direction: resolveFlow(props.form, 'specie_uuid'),
    }
  }

  componentDidMount() {
    const { specieContext, shelterContext, form } = this.props

    // Initial values may settle after the Form mounts — re-resolve direction.
    const direction = resolveFlow(form, 'specie_uuid')
    if (direction !== this.state.direction) {
      this.setState({ direction })
    }

    if (!specieContext.fetching && !specieContext.species?.length) {
      specieContext.requestSpecieList({
        options: {
          limit: 9999,
          sort: ['name'],
        },
        search: {
          _fields: ["uuid", "name"],
        }
      })
    }

    if (!shelterContext.fetching && !shelterContext.shelters?.length) {
      shelterContext.requestShelterList({
        options: {
          limit: 9999,
          sort: ['name'],
        },
        search: {
          _fields: ["id", "uuid", "name"],
          active: true,
          deleted: false,
          organization_integrations: {
            with: {
              _fields: ["id", "uuid", "description", "partner_key", "main", "shelter_id"],
              active: true,
              deleted: false,
            }
          }
        }
      })
    }
  }

  /** Resolve a species name for display, from the loaded species list. */
  specieName = (uuid?: string) =>
    this.props.specieContext.species?.find(s => s.uuid === uuid)?.name

  /** Resolve a shelter name for display, from the loaded shelter list. */
  shelterName = (uuid?: string) =>
    this.props.shelterContext.shelters?.find(s => s.uuid === uuid)?.name

  /** Resolve a partner display name. */
  partnerName = (key?: string) =>
    this.props.partnerList?.find(p => p.key === key)?.name || key

  // ----- Field groups (shared between directions) -----

  renderSpeciesField = () => {
    const { specieContext } = this.props
    const speciesOptions = specieContext.species?.map(specie => ({
      label: specie.name,
      value: specie.uuid,
    }))

    return (
      <Form.Item
        label="Species"
        name="specie_uuid"
        rules={[{ required: true, message: 'Missing a species' }]}
      >
        <Select
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
          loading={specieContext.fetching}
          placeholder="Select a species"
          options={speciesOptions}
        />
      </Form.Item>
    )
  }

  /** External target (Shelter Buddy class → type pickers, or the default name + id inputs). */
  renderExternalTarget = () => {
    const { form } = this.props

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) =>
          prev.shelter_uuid !== cur.shelter_uuid ||
          prev.partner_key !== cur.partner_key ||
          prev.integration_uuid !== cur.integration_uuid
        }
      >
        {({ getFieldValue }) => {
          const shelterUuid: string | undefined = getFieldValue("shelter_uuid")
          const partnerKey: string | undefined = getFieldValue("partner_key")
          const integrationUuid: string | undefined = getFieldValue("integration_uuid")

          const canLoadExternal =
            !!shelterUuid && !!integrationUuid && partnerKey === "SHELTER_BUDDY"

          if (canLoadExternal) {
            return (
              <ShelterBuddyAnimalTypeField form={form} integrationUuid={integrationUuid!} />
            )
          }

          return <ManualExternalInputs />
        }}
      </Form.Item>
    )
  }

  // ----- Inbound-only: read-only external info -----

  /** Read-only display of the incoming external value, with hidden fields to keep its values in the payload. */
  renderIncoming = () => (
    <IncomingStep
      form={this.props.form}
      partnerList={this.props.partnerList}
      externalRowLabel="External Type"
    />
  )

  // ----- Review (both directions) -----

  renderReview = () => {
    const { form } = this.props

    const partnerKey = form.getFieldValue('partner_key')
    const specieUuid = form.getFieldValue('specie_uuid')
    const shelterUuid = form.getFieldValue('shelter_uuid')
    const name = form.getFieldValue('name')
    const externalId = form.getFieldValue('external_id')

    const externalSide = [name, externalId].filter(Boolean).join(' · ') || '—'
    const mappingSummary = `${this.specieName(specieUuid) || '—'} ↔ ${externalSide}`

    return (
      <>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Direction">
            {DIRECTION_LABEL.BOTH}
          </Descriptions.Item>
          <Descriptions.Item label="Partner">{this.partnerName(partnerKey) || '—'}</Descriptions.Item>
          <Descriptions.Item label="Mapping">{mappingSummary}</Descriptions.Item>
          <Descriptions.Item label="Applies to">
            {shelterUuid ? (this.shelterName(shelterUuid) || shelterUuid) : <Tag>GLOBAL</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <ActiveField />
      </>
    )
  }

  // ----- Step definitions -----

  outboundSteps = (): Step[] => {
    const { form, shelterContext } = this.props

    return [
      {
        key: 'our-side',
        title: 'Our side',
        fields: ['specie_uuid', 'partner_key', 'shelter_uuid'],
        content: () => (
          <>
            {this.renderSpeciesField()}
            <PartnerField partnerList={this.props.partnerList} />
            <ScopeField form={form} shelterContext={shelterContext} />
          </>
        ),
      },
      {
        key: 'external',
        title: 'External target',
        fields: ['name', 'external_id'],
        content: () => (
          <>
            <IntegrationField shelterContext={shelterContext} />
            {this.renderExternalTarget()}
          </>
        ),
      },
      {
        key: 'review',
        title: 'Review & Save',
        fields: [],
        content: this.renderReview,
      },
    ]
  }

  inboundSteps = (): Step[] => {
    const { form, shelterContext } = this.props

    return [
      {
        key: 'incoming',
        title: 'Incoming',
        fields: [],
        content: this.renderIncoming,
      },
      {
        key: 'our-side',
        title: 'Our side',
        fields: ['specie_uuid', 'shelter_uuid'],
        content: () => (
          <>
            {this.renderSpeciesField()}
            <ScopeField form={form} shelterContext={shelterContext} />
          </>
        ),
      },
      {
        key: 'review',
        title: 'Review & Save',
        fields: [],
        content: this.renderReview,
      },
    ]
  }

  render() {
    const { direction } = this.state
    const steps = direction === 'inbound' ? this.inboundSteps() : this.outboundSteps()

    return <MappingStepper form={this.props.form} steps={steps} loading={this.props.loading} />
  }
}

export default class MappingSpecieForm extends Component<MappingSpecieFormProps> {
  render() {
    return (
      <SpecieConsumer>
        {(specieContext) =>
          <ShelterConsumer>
            {(shelterContext) =>
              <MappingSpecieFormInner
                form={this.props.form}
                partnerList={this.props.partnerList}
                loading={this.props.loading}
                specieContext={specieContext}
                shelterContext={shelterContext}
              />
            }
          </ShelterConsumer>
        }
      </SpecieConsumer>
    )
  }
}
